import { HttpErrorResponse, HttpEvent, HttpInterceptorFn, HttpRequest, HttpResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, switchMap, tap, throwError } from 'rxjs';
import { SessionService } from '../services/session.service';
import { WidgetTokenService } from '../services/widget-token.service';

export const sessionInterceptor: HttpInterceptorFn = (req, next) => {
  const tokens = inject(WidgetTokenService);
  const session = inject(SessionService);

  const withIdentity = (request: HttpRequest<unknown>) => {
    const headers: Record<string, string> = {};

    const sessionId = session.id;
    if (sessionId) headers['X-Session-Id'] = sessionId;

    const widgetToken = tokens.token;
    if (widgetToken) headers['X-Widget-Token'] = widgetToken;

    return Object.keys(headers).length > 0 ? request.clone({ setHeaders: headers }) : request;
  };

  const captureSessionId = tap<HttpEvent<unknown>>(event => {
    if (event instanceof HttpResponse) {
      session.store(event.headers.get('x-session-id'));
    }
  });

  return next(withIdentity(req)).pipe(
    captureSessionId,
    catchError((error: unknown) => {
      if (!(error instanceof HttpErrorResponse) || error.status !== 403) {
        return throwError(() => error);
      }

      // Ask the host for a fresh token and send the same request again. Nothing reloads, so the
      // conversation on screen is untouched and this request is delivered rather than dropped.
      //
      // The retry goes straight to `next`, so a second 403 propagates to the caller instead of
      // looping back through here.
      return tokens.renew().pipe(
        switchMap(() => next(withIdentity(req)).pipe(captureSessionId)),
        catchError(() => throwError(() => error))
      );
    })
  );
};
